'use client';

import { useState } from 'react';
import { Search, ArrowRight } from 'lucide-react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import type { PartnersDiagnosisContent } from '@/lib/ui-content/partners-page-content';
import { buildGeoracleUrl } from '@/lib/georacle-link';

type Props = {
  locale: LocaleDetails;
  content: PartnersDiagnosisContent;
};

/** Accepts a bare domain ("yourbrand.com") as well as a full URL — normalizes to https://. */
function normalizeUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    if (!url.hostname.includes('.')) return null;
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * The live AI-visibility check: this page's one moment of depth (rule 4 of the
 * Inception line). It is a real tool, so it is allowed to be a bounded object
 * on the canvas, but the corner brackets and the blue button are gone.
 *
 * Redirects straight into georacle.ai's own /tools page with `?url=`, which
 * pre-fills and auto-starts the analysis on their end (confirmed by reading
 * their code), so there is no separate landing step.
 */
export function GeoDiagnosisPanel({ locale, content }: Props) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = normalizeUrl(value);
    if (!normalized) {
      setError(true);
      return;
    }
    setError(false);
    const target = buildGeoracleUrl('tools', locale.languageCode, {
      url: normalized,
      source: 'inceptionai-partners',
    });
    window.open(target, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="rounded-card border border-hairline bg-surface p-8 sm:p-10 dark:bg-card/60">
      <div className="flex items-start gap-4">
        <span className="mt-1 shrink-0 text-muted">
          <Search className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <h3 className="font-display text-2xl font-normal leading-snug text-ink sm:text-3xl">
            {content.heading}
          </h3>
          <p className="mt-3 max-w-[52ch] text-base font-light leading-relaxed text-muted">
            {content.description}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-end" noValidate>
        <div className="w-full">
          <label htmlFor="geo-diagnosis-url" className="sr-only">
            {content.inputPlaceholder}
          </label>
          <input
            id="geo-diagnosis-url"
            type="text"
            inputMode="url"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError(false);
            }}
            placeholder={content.inputPlaceholder}
            aria-invalid={error}
            className="w-full rounded-none border-0 border-b border-hairline bg-transparent px-0 py-2.5 text-base font-light text-ink placeholder-muted/50 transition-colors duration-200 focus:border-accent focus:outline-none focus:ring-0"
          />
        </div>
        <button
          type="submit"
          className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-ink px-8 py-3.5 text-sm font-medium text-card transition-colors duration-200 hover:bg-accent-strong"
        >
          {content.submitLabel}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </button>
      </form>
      {error && <p role="alert" className="mt-3 text-sm font-light text-danger">{content.invalidUrlError}</p>}

      <p className="mt-8 border-t border-hairline pt-5 text-xs font-light leading-relaxed text-muted">
        {content.disclosure}
      </p>
    </div>
  );
}
